<template>
  <div class="bom-page">
    <template v-if="!result">
		<section class="bom-hero pc-container">
        <img src="/bom.jpg" alt="BOM配单海报">
      </section>

      <section class="upload-card">
        <div
          class="drop-zone"
          :class="{ dragging: dragging }"
          @click="chooseFile"
          @dragenter.prevent="dragging = true"
          @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="handleDrop"
        >
          <input ref="fileInput" type="file" accept=".xlsx,.xls" @change="handleFileChange">
          <p>{{ loading ? '正在解析并匹配商品，请稍候...' : '点击或将文件拖拽到框内上传' }}</p>
          <button type="button" :disabled="loading">
            <span class="excel-icon">X</span>{{ loading ? '匹配中' : '上传BOM' }}
          </button>
        </div>
        <div class="upload-note">
          <span>建议使用模板导入以便获得最优匹配</span>
          <a @click.stop="downloadTemplate">下载模板</a>
        </div>
      </section>
    </template>

    <section v-else class="bom-result pc-container">
      <header class="result-head">
        <div>
          <h1>{{ result.fileName }}</h1>
          <p>{{ result.bomNo }} <span>数据匹配行：{{ rows.length }}条</span></p>
        </div>
        <div class="head-actions">
          <button class="primary" @click="exportQuote">导出报价单</button>
          <button @click="resetUpload">重新上传</button>
          <button @click="manualMatch">人工配单</button>
        </div>
      </header>

      <div class="result-toolbar">
        <div class="status-tabs">
          <button :class="{ active: activeFilter === 'ALL' }" @click="activeFilter = 'ALL'">全部 <b>{{ rows.length }}</b></button>
          <button :class="{ active: activeFilter === 'EXACT' }" @click="activeFilter = 'EXACT'">完全匹配 <b>{{ statusCount('EXACT') }}</b></button>
          <button :class="{ active: activeFilter === 'PENDING' }" @click="activeFilter = 'PENDING'">待确认 <b>{{ statusCount('PENDING') }}</b></button>
          <button :class="{ active: activeFilter === 'UNMATCHED' }" @click="activeFilter = 'UNMATCHED'">无法匹配 <b>{{ statusCount('UNMATCHED') }}</b></button>
        </div>
        <span>采购套数：<input v-model.number="purchaseSets" type="number" min="1" @change="applyPurchaseSets"></span>
      </div>

      <div class="table-scroll">
        <table class="match-table">
          <thead>
            <tr>
              <th class="check-col"><input type="checkbox" :checked="allVisibleSelected" @change="toggleVisible"></th>
              <th>序号</th>
              <th>原始需求</th>
              <th>整理后的需求</th>
              <th>单用量</th>
              <th>结果</th>
              <th>匹配商品</th>
              <th>库存</th>
              <th>购买数量</th>
              <th>单价（含税）</th>
              <th>小计（含税）</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in filteredRows" :key="row.rowNumber">
              <td><input type="checkbox" :disabled="!row.product || !row.product.hasPrice" :checked="isSelected(row)" @change="toggleRow(row)"></td>
              <td>{{ index + 1 }}</td>
              <td class="requirement-cell">
                <strong>{{ row.requirement.model || '未填写型号' }}</strong>
                <span>{{ row.requirement.specification || '未填写规格参数' }}</span>
                <span v-if="row.requirement.customCode">自定义编号：{{ row.requirement.customCode }}</span>
              </td>
              <td class="requirement-cell">
                <span>型号：{{ row.requirement.model || '-' }}</span>
                <span>品牌：{{ row.requirement.brand || '-' }}</span>
                <span>封装：{{ row.requirement.packageName || '-' }}</span>
                <span>类别：{{ row.requirement.category || '-' }}</span>
              </td>
              <td>{{ row.requirement.unitQuantity || row.quantity }}</td>
              <td>
                <span class="status-mark" :class="row.matchStatus.toLowerCase()">{{ statusIcon(row.matchStatus) }}</span>
                <small>{{ statusText(row.matchStatus) }}</small>
              </td>
              <td class="product-cell">
                <template v-if="row.product">
                  <strong>{{ productTitle(row.product) }}</strong>
                  <span v-if="productType(row.product)">产品类型：{{ productType(row.product) }}</span>
                  <span>品牌：{{ productBrand(row.product) }}</span>
                  <span>分类：{{ row.product.categoryName }} / {{ row.product.subCategoryName }}</span>
                  <span>{{ row.matchMessage }}</span>
                </template>
                <span v-else>{{ row.matchMessage }}</span>
              </td>
              <td><b v-if="row.product" class="stock-number">{{ row.product.stock }}</b><span v-else>-</span></td>
              <td>
                <input v-if="row.product" v-model.number="row.quantity" class="quantity-input" type="number" :min="row.product.min" @change="normalizeQuantity(row)">
                <span v-else>-</span>
              </td>
              <td><span v-if="row.product && row.product.hasPrice">¥ {{ rowUnitPrice(row) }}</span><span v-else-if="row.product">暂无价格/联系询价</span><span v-else>-</span></td>
              <td><b v-if="row.product" class="subtotal">¥ {{ rowSubtotal(row) }}</b><span v-else>-</span></td>
              <td><button class="replace-btn" @click="replaceProduct(row)">{{ row.product ? '更换型号' : '查找商品' }}</button></td>
            </tr>
          </tbody>
        </table>
        <div v-if="!filteredRows.length" class="empty-result">当前分类暂无数据</div>
      </div>

      <footer class="result-footer">
        <div class="footer-actions">
          <label><input type="checkbox" :checked="allVisibleSelected" @change="toggleVisible"> 全选</label>
          <button @click="removeSelected">删除</button>
          <button @click="addMaterial">新增物料信息</button>
        </div>
        <div class="settle-actions">
          <span>已选择 <b>{{ selectedRows.length }}</b> 项</span>
          <span>总价：<strong>¥ {{ selectedTotal }}</strong></span>
          <button class="cart-button" @click="addSelectedToCart">加入购物车</button>
          <button class="order-button" @click="oneClickOrder">一键下单</button>
        </div>
      </footer>
    </section>
  </div>
</template>

<script>
  import { message } from 'ant-design-vue'
  import { getPcMallSettings, getPcMemberBomRecord, uploadPcBom, submitPcBomManual, downloadPcBomTemplate } from '@/api/pc'
  import { downloadBlobResponse } from '@/utils/downloadFile'
  import commerce from '@/utils/pcCommerce'
  import { productTitle, productType, productBrand } from '@/utils/pcProductDisplay'
  import { addCartItem } from '@/utils/pcCart'

  export default {
    name: 'PcBomMatch',
    data() {
      return {
          companyName: '锐高科技',
        loading: false,
        dragging: false,
        result: null,
        rows: [],
        selectedMap: {},
        activeFilter: 'ALL',
        purchaseSets: 1
      }
    },
    computed: {
      filteredRows() {
        return this.activeFilter === 'ALL' ? this.rows : this.rows.filter(row => row.matchStatus === this.activeFilter)
      },
      selectedRows() {
        return this.rows.filter(row => row.product && this.selectedMap[row.rowNumber])
      },
      allVisibleSelected() {
        const selectable = this.filteredRows.filter(row => row.product)
        return selectable.length > 0 && selectable.every(this.isSelected)
      },
      selectedTotal() {
        const total = this.selectedRows.reduce((sum, row) => sum + Number(this.rowSubtotal(row)), 0)
        return commerce.formatMoney(total, 2)
      }
    },
    created() {
      getPcMallSettings().then(data => { this.companyName = (data && data.companyName) || this.companyName }).catch(() => {})
      if (this.$route.query.recordId) this.loadRecord(this.$route.query.recordId)
    },
    methods: {
      productTitle,
      productType,
      productBrand,
      async downloadTemplate() {
        try { await downloadBlobResponse(await downloadPcBomTemplate(), `${this.companyName}BOM模板.xlsx`) }
        catch (error) { message.error(error.message || 'BOM模板下载失败') }
      },
      chooseFile() {
        if (!this.loading) this.$refs.fileInput.click()
      },
      handleFileChange(event) {
        const file = event.target.files && event.target.files[0]
        if (file) this.upload(file)
        event.target.value = ''
      },
      handleDrop(event) {
        this.dragging = false
        const file = event.dataTransfer.files && event.dataTransfer.files[0]
        if (file) this.upload(file)
      },
      async upload(file) {
        if (!/\.(xlsx|xls)$/i.test(file.name)) {
          message.error('仅支持上传.xlsx或.xls格式的BOM文件')
          return
        }
        if (file.size > 10 * 1024 * 1024) {
          message.error('BOM文件大小不能超过10MB')
          return
        }
        this.loading = true
        try {
          const data = await uploadPcBom(file)
          this.applyResult(data)
          message.success(`已完成${this.rows.length}条物料匹配`)
        } catch (error) {
          message.error(error.message || 'BOM上传解析失败')
        } finally {
          this.loading = false
        }
      },
      async loadRecord(id) {
        this.loading = true
        try {
          const data = await getPcMemberBomRecord(id)
          this.applyResult(data && data.result ? data.result : null)
          if (this.result) this.result.recordId = Number(id)
        } catch (error) {
          message.error(error.message || 'BOM记录加载失败')
        } finally {
          this.loading = false
        }
      },
      applyResult(data) {
        if (!data) return
        this.result = data
        this.rows = (Array.isArray(data.rows) ? data.rows : []).map(row => ({
          ...row,
          product: row.product ? commerce.normalizeProduct({
            ...row.product,
            packageName: (row.requirement && row.requirement.packageName) || row.product.packageName
          }) : null,
          quantity: Number(row.requestedQuantity || 1)
        }))
        this.selectedMap = {}
        this.activeFilter = 'ALL'
        this.purchaseSets = 1
      },
      resetUpload() {
        this.result = null
        this.rows = []
        this.selectedMap = {}
      },
      statusCount(status) {
        return this.rows.filter(row => row.matchStatus === status).length
      },
      statusText(status) {
        return { EXACT: '完全匹配', PENDING: '待确认', UNMATCHED: '无法匹配' }[status] || '无法匹配'
      },
      statusIcon(status) {
        return status === 'EXACT' ? '✓' : status === 'PENDING' ? '!' : '×'
      },
      isSelected(row) {
        return Boolean(this.selectedMap[row.rowNumber])
      },
      toggleRow(row) {
        if (row.product && row.product.hasPrice) this.$set(this.selectedMap, row.rowNumber, !this.isSelected(row))
      },
      toggleVisible() {
        const shouldSelect = !this.allVisibleSelected
        this.filteredRows.filter(row => row.product).forEach(row => {
          this.$set(this.selectedMap, row.rowNumber, shouldSelect)
        })
      },
      applyPurchaseSets() {
        this.purchaseSets = Math.max(1, Number(this.purchaseSets || 1))
        this.rows.forEach(row => {
          if (!row.product) return
          const baseQuantity = Number(row.requirement.unitQuantity || row.product.min || 1)
          row.quantity = Math.max(row.product.min || 1, baseQuantity * this.purchaseSets)
        })
      },
      normalizeQuantity(row) {
        row.quantity = Math.max(row.product.min || 1, Number(row.quantity || row.product.min || 1))
      },
      rowUnitPrice(row) {
        return commerce.formatMoney(commerce.getTierPrice(row.product, row.quantity), 4)
      },
      rowSubtotal(row) {
        return commerce.formatMoney(commerce.getTierPrice(row.product, row.quantity) * Number(row.quantity || 0), 2)
      },
      addSelectedToCart() {
        if (!this.selectedRows.length) {
          message.warning('请先选择需要加入购物车的匹配商品')
          return false
        }
        this.selectedRows.forEach(row => addCartItem(row.product, row.quantity))
        this.$emit('cart-change')
        message.success(`已将${this.selectedRows.length}项商品加入购物车`)
        return true
      },
      oneClickOrder() {
        if (this.addSelectedToCart()) this.$router.push('/pc/cart')
      },
      removeSelected() {
        if (!this.selectedRows.length) {
          message.warning('请先选择需要删除的物料')
          return
        }
        const selected = this.selectedMap
        this.rows = this.rows.filter(row => !selected[row.rowNumber])
        this.selectedMap = {}
      },
      replaceProduct(row) {
        this.$router.push({ path: '/pc/products', query: { keyword: row.requirement.model || row.requirement.specification } })
      },
      addMaterial() {
        message.info('请在标准模板中补充物料后重新上传，系统会重新匹配')
      },
      async manualMatch() {
        if (!this.result || !this.result.recordId) return message.warning('请先登录并重新上传BOM后再提交人工处理')
        const contactName = window.prompt('请输入联系人姓名')
        if (!contactName) return
        const contactPhone = window.prompt('请输入联系电话')
        if (!contactPhone) return
        const memberRemark = window.prompt('请补充人工配单需求（可选）') || ''
        try {
          await submitPcBomManual(this.result.recordId, { contactName, contactPhone, memberRemark })
          message.success('人工配单申请已提交，后台可查看并处理')
        } catch (error) { message.error(error.message || '人工配单申请提交失败') }
      },
      exportQuote() {
        const lines = [['序号', '自定义编号', '需求型号', '匹配型号', '匹配结果', '购买数量', '单价', '小计']]
        this.rows.forEach((row, index) => {
          lines.push([
            index + 1,
            row.requirement.customCode || '',
            row.requirement.model || '',
            row.product ? productTitle(row.product) : '',
            this.statusText(row.matchStatus),
            row.product ? row.quantity : '',
            row.product ? this.rowUnitPrice(row) : '',
            row.product ? this.rowSubtotal(row) : ''
          ])
        })
        const csv = '\ufeff' + lines.map(line => line.map(value => `"${String(value).replace(/"/g, '""')}"`).join(',')).join('\r\n')
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
        const link = document.createElement('a')
        link.href = url
        link.download = `${this.result.bomNo}-报价单.csv`
        link.click()
        URL.revokeObjectURL(url)
      }
    }
  }
</script>

<style scoped>
  .bom-page {
    min-height: calc(100vh - 206px);
    background: #f4f6f9;
  }

  .bom-hero {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: #f4f6f9;
  }

  .bom-hero img {
    display: block;
    width: 100%;
    height: 480px;
    object-fit: cover;
    object-position: center top;
    opacity: 1;
  }

  .upload-card {
    position: relative;
    z-index: 2;
    width: min(720px, calc(100% - 32px));
    margin: -39px auto 70px;
    padding: 20px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 8px 28px rgba(15, 54, 96, .16);
    box-sizing: border-box;
  }

  .drop-zone {
    height: 284px;
    border: 1px dashed #cbd5e1;
    border-radius: 10px 10px 0 0;
    background: #f8fafc;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: border-color .2s ease, background-color .2s ease;
  }

  .drop-zone.dragging {
    border-color: #1684ff;
    background: #edf6ff;
  }

  .drop-zone input {
    display: none;
  }

  .drop-zone p {
    margin: 0 0 14px;
    color: #475569;
  }

  .drop-zone button {
    width: 208px;
    height: 56px;
    border: 0;
    border-radius: 7px;
    background: #1684ff;
    color: #fff;
    font-size: 20px;
    font-weight: 700;
    cursor: pointer;
  }

  .drop-zone button:disabled {
    opacity: .7;
  }

  .excel-icon {
    display: inline-flex;
    width: 23px;
    height: 25px;
    margin-right: 8px;
    border: 2px solid #fff;
    align-items: center;
    justify-content: center;
    font-size: 13px;
  }

  .upload-note {
    height: 52px;
    padding: 0 4px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #7b8494;
    font-size: 13px;
  }

  .upload-note a {
    color: #1684ff;
    font-size: 17px;
    font-weight: 700;
  }

  .bom-result {
    padding: 20px 0 86px;
  }

  .result-head {
    min-height: 74px;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #e9edf5;
    box-sizing: border-box;
  }

  .result-head h1 {
    margin: 0 0 5px;
    color: #1f2937;
    font-size: 17px;
  }

  .result-head p {
    margin: 0;
    color: #475569;
  }

  .result-head p span {
    margin-left: 18px;
  }

  .head-actions {
    display: flex;
    gap: 9px;
  }

  .head-actions button,
  .footer-actions button,
  .replace-btn {
    height: 34px;
    padding: 0 18px;
    border: 1px solid #d5dbe5;
    border-radius: 4px;
    background: #fff;
    color: #344054;
    cursor: pointer;
  }

  .head-actions .primary {
    border-color: #1684ff;
    background: #1684ff;
    color: #fff;
  }

  .result-toolbar {
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff;
  }

  .status-tabs {
    height: 50px;
    display: flex;
  }

  .status-tabs button {
    min-width: 130px;
    border: 0;
    border-bottom: 2px solid transparent;
    background: #fff;
    color: #1f2937;
    cursor: pointer;
  }

  .status-tabs button.active {
    border-bottom-color: #1684ff;
    color: #1684ff;
  }

  .status-tabs b {
    color: inherit;
  }

  .result-toolbar > span {
    margin-right: 16px;
    color: #667085;
  }

  .result-toolbar input {
    width: 76px;
    height: 30px;
    margin-left: 6px;
    border: 1px solid #d5dbe5;
    border-radius: 5px;
    text-align: center;
  }

  .table-scroll {
    overflow-x: auto;
    background: #fff;
  }

  .match-table {
    width: 100%;
    min-width: 1580px;
    border-collapse: collapse;
    table-layout: fixed;
  }

  .match-table th,
  .match-table td {
    padding: 10px 9px;
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    text-align: center;
    vertical-align: middle;
    box-sizing: border-box;
  }

  .match-table th {
    height: 48px;
    background: #f4f6f9;
    color: #1f2937;
    font-size: 14px;
  }

  .match-table td {
    min-height: 128px;
    color: #344054;
    font-size: 13px;
  }

  .match-table th:nth-child(1) { width: 42px; }
  .match-table th:nth-child(2) { width: 52px; }
  .match-table th:nth-child(3) { width: 190px; }
  .match-table th:nth-child(4) { width: 210px; }
  .match-table th:nth-child(5) { width: 82px; }
  .match-table th:nth-child(6) { width: 94px; }
  .match-table th:nth-child(7) { width: 250px; }
  .match-table th:nth-child(8) { width: 105px; }
  .match-table th:nth-child(9) { width: 112px; }
  .match-table th:nth-child(10) { width: 120px; }
  .match-table th:nth-child(11) { width: 125px; }
  .match-table th:nth-child(12) { width: 125px; }

  .requirement-cell,
  .product-cell {
    text-align: left !important;
  }

  .requirement-cell strong,
  .requirement-cell span,
  .product-cell strong,
  .product-cell span {
    display: block;
    margin: 4px 0;
    line-height: 18px;
    word-break: break-word;
  }

  .product-cell strong {
    color: #1684ff;
  }

  .status-mark {
    width: 22px;
    height: 22px;
    margin: 0 auto 5px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #22c55e;
    color: #fff;
    font-weight: 700;
  }

  .status-mark.pending { background: #f59e0b; }
  .status-mark.unmatched { background: #ef4444; }

  .match-table small {
    color: #667085;
  }

  .stock-number {
    color: #1684ff;
    font-size: 17px;
  }

  .quantity-input {
    width: 78px;
    height: 34px;
    border: 1px solid #d5dbe5;
    border-radius: 5px;
    text-align: center;
  }

  .subtotal {
    color: #ff4d00;
    font-size: 15px;
  }

  .replace-btn {
    padding: 0 12px;
  }

  .empty-result {
    padding: 80px 0;
    text-align: center;
    color: #8a8f99;
  }

  .result-footer {
    position: sticky;
    z-index: 4;
    bottom: 0;
    min-height: 72px;
    padding: 0 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid #e5e7eb;
    background: #fff;
    box-shadow: 0 -5px 18px rgba(15, 23, 42, .06);
  }

  .footer-actions,
  .settle-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .settle-actions b,
  .settle-actions strong {
    color: #ff4d00;
  }

  .settle-actions strong {
    font-size: 22px;
  }

  .cart-button,
  .order-button {
    height: 42px;
    padding: 0 24px;
    border: 1px solid #1684ff;
    border-radius: 6px;
    background: #fff;
    color: #1684ff;
    font-weight: 700;
    cursor: pointer;
  }

  .order-button {
    background: #1684ff;
    color: #fff;
  }
</style>
